// 1.D.6 + 2.E.3 — Faceted-filterin tila ja URL-paramien serialisointi.
// Pidetaan puhtaat funktiot erikseen jotta voidaan unit-testata
// ilman DOMia. Wouter:n useSearch-hook palauttaa raw search-stringin
// ilman alkavaa "?":a.

export type FilterState = {
  projectId?: string;
  tier?: string;
  metaStatus?: string;
  tags?: string;
  // 2.E.2 — hakukentta + faceted-suodattimet
  q?: string;
  ratingMin?: string; // "0".."5"
  ratingMax?: string;
  flag?: string; // "pick" | "reject" | "unflagged"
  colorLabel?: string; // "red" | "yellow" | "green" | "blue" | "purple" | "none"
  capturedFrom?: string; // ISO date
  capturedTo?: string;
  // Post-3.B path-prefix-filter — rajaa listaa current_path-alipuuhun
  path?: string; // absoluuttinen polku /-alkuinen
};

export const TIER_OPTIONS = ["active", "archived", "offloaded", "excluded"] as const;
export const META_STATUS_OPTIONS = ["none", "auto", "pending", "complete"] as const;
export const FLAG_OPTIONS = ["pick", "reject", "unflagged"] as const;
export const COLOR_OPTIONS = ["red", "yellow", "green", "blue", "purple", "none"] as const;

const VALID_KEYS: ReadonlySet<keyof FilterState> = new Set([
  "projectId",
  "tier",
  "metaStatus",
  "tags",
  "q",
  "ratingMin",
  "ratingMax",
  "flag",
  "colorLabel",
  "capturedFrom",
  "capturedTo",
  "path",
]);
const TIER_SET: ReadonlySet<string> = new Set(TIER_OPTIONS);
const META_SET: ReadonlySet<string> = new Set(META_STATUS_OPTIONS);
const FLAG_SET: ReadonlySet<string> = new Set(FLAG_OPTIONS);
const COLOR_SET: ReadonlySet<string> = new Set(COLOR_OPTIONS);

/**
 * Lue URL search-string FilterState:ksi. Tunnistaa vain validit kentat
 * ja sallitut enum-arvot — tuntemattomat arvot pudotetaan jotta
 * URL-manipulointi ei voi paasta lapi tahroja serverille.
 */
export function parseFilters(search: string): FilterState {
  const params = new URLSearchParams(search.replace(/^\?/, ""));
  const out: FilterState = {};
  for (const [key, value] of params.entries()) {
    if (!VALID_KEYS.has(key as keyof FilterState)) continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (key === "tier" && !TIER_SET.has(trimmed)) continue;
    if (key === "metaStatus" && !META_SET.has(trimmed)) continue;
    if (key === "flag" && !FLAG_SET.has(trimmed)) continue;
    if (key === "colorLabel" && !COLOR_SET.has(trimmed)) continue;
    if (key === "ratingMin" || key === "ratingMax") {
      const n = Number.parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n < 0 || n > 5) continue;
    }
    // path: vaatii /-alkua, suojaa URL-manipulointia vastaan
    if (key === "path" && !trimmed.startsWith("/")) continue;
    (out as Record<string, string>)[key] = trimmed;
  }
  return out;
}

/**
 * Muodosta URL search-string FilterState:sta. Stabili jarjestys.
 */
export function buildSearch(filters: FilterState): string {
  const params = new URLSearchParams();
  for (const key of [
    "q",
    "path",
    "projectId",
    "tier",
    "metaStatus",
    "tags",
    "ratingMin",
    "ratingMax",
    "flag",
    "colorLabel",
    "capturedFrom",
    "capturedTo",
  ] as const) {
    const v = filters[key];
    if (v === undefined || v === "") continue;
    params.set(key, v);
  }
  return params.toString();
}

/** Onko filterissa yhtaan ehtoa asetettuna. */
export function hasActiveFilters(filters: FilterState): boolean {
  return Object.values(filters).some((v) => v !== undefined && v !== "");
}
